import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Scale, DollarSign, Calendar, Check, X, Eye, Settings, TrendingUp, Activity, BarChart3, AlertTriangle, Trash2, Ban, UserCheck, History, Shield, ShieldAlert, ShieldCheck, Lock, Unlock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { api, useAuth } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { formatCurrency } from '../utils/currency';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [visitorStats, setVisitorStats] = useState(null);
  const [pendingLawyers, setPendingLawyers] = useState([]);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commissionModal, setCommissionModal] = useState(false);
  const [newCommission, setNewCommission] = useState(10);
  const [lawyerDetailModal, setLawyerDetailModal] = useState({ open: false, lawyer: null });
  
  // User management modals
  const [suspendModal, setSuspendModal] = useState({ open: false, user: null });
  const [removeModal, setRemoveModal] = useState({ open: false, user: null });
  const [actionLogsModal, setActionLogsModal] = useState({ open: false, user: null, logs: [] });
  const [suspendReason, setSuspendReason] = useState('');
  const [removeReason, setRemoveReason] = useState('');
  const [archiveOnRemove, setArchiveOnRemove] = useState(true);

  // Security dashboard state
  const [securityStats, setSecurityStats] = useState(null);
  const [securityLogs, setSecurityLogs] = useState([]);
  const [securityLoading, setSecurityLoading] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [statsRes, lawyersRes, usersRes, bookingsRes, paymentsRes, visitorRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/lawyers/pending'),
        api.get('/admin/users?limit=50'),
        api.get('/admin/bookings?limit=50'),
        api.get('/admin/payments?limit=50'),
        api.get('/analytics/summary')
      ]);
      
      setStats(statsRes.data);
      setPendingLawyers(lawyersRes.data || []);
      setUsers(usersRes.data.users || []);
      setBookings(bookingsRes.data.bookings || []);
      setPayments(paymentsRes.data.payments || []);
      setNewCommission(statsRes.data.commission_rate);
      setVisitorStats(visitorRes.data);
    } catch (err) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const approveLawyer = async (lawyerId) => {
    try {
      await api.put(`/admin/lawyers/${lawyerId}/approve`);
      toast.success('Lawyer approved');
      fetchAllData();
    } catch (err) {
      toast.error('Failed to approve lawyer');
    }
  };

  const rejectLawyer = async (lawyerId) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;
    
    try {
      await api.put(`/admin/lawyers/${lawyerId}/reject`, { reason });
      toast.success('Lawyer rejected');
      fetchAllData();
    } catch (err) {
      toast.error('Failed to reject lawyer');
    }
  };

  // Enhanced suspend user function
  const suspendUser = async () => {
    if (!suspendModal.user) return;
    
    try {
      await api.put(`/admin/users/${suspendModal.user.id}/suspend`, { 
        reason: suspendReason || 'No reason provided' 
      });
      toast.success(`${suspendModal.user.name} has been suspended`);
      setSuspendModal({ open: false, user: null });
      setSuspendReason('');
      fetchAllData();
    } catch (err) {
      toast.error('Failed to suspend user');
    }
  };

  // Activate user function
  const activateUser = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}/activate`);
      toast.success('User activated successfully');
      fetchAllData();
    } catch (err) {
      toast.error('Failed to activate user');
    }
  };

  // Remove user function
  const removeUser = async () => {
    if (!removeModal.user) return;
    
    try {
      await api.delete(`/admin/users/${removeModal.user.id}/remove`, {
        data: { 
          reason: removeReason || 'No reason provided',
          archive: archiveOnRemove
        }
      });
      toast.success(`${removeModal.user.name} has been permanently removed`);
      setRemoveModal({ open: false, user: null });
      setRemoveReason('');
      setArchiveOnRemove(true);
      fetchAllData();
    } catch (err) {
      toast.error('Failed to remove user');
    }
  };

  // Fetch action logs for a user
  const fetchActionLogs = async (targetUser) => {
    try {
      const res = await api.get(`/admin/users/${targetUser.id}/action-logs`);
      setActionLogsModal({ open: true, user: targetUser, logs: res.data.logs || [] });
    } catch (err) {
      toast.error('Failed to fetch action logs');
    }
  };

  const updateCommission = async () => {
    try {
      await api.put('/admin/settings/commission', { commission_percentage: newCommission });
      toast.success('Commission updated');
      setCommissionModal(false);
      fetchAllData();
    } catch (err) {
      toast.error('Failed to update commission');
    }
  };

  // Fetch security data
  const fetchSecurityData = async () => {
    setSecurityLoading(true);
    try {
      const [statsRes, logsRes] = await Promise.all([
        api.get('/admin/security-stats'),
        api.get('/admin/security-logs?limit=50')
      ]);
      setSecurityStats(statsRes.data);
      setSecurityLogs(logsRes.data.logs || []);
    } catch (err) {
      toast.error('Failed to load security data');
    } finally {
      setSecurityLoading(false);
    }
  };

  // Get event type badge styling
  const getEventBadge = (eventType) => {
    const badges = {
      'login_success': { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: ShieldCheck },
      'login_failed': { bg: 'bg-red-100', text: 'text-red-700', icon: ShieldAlert },
      'login_lockout': { bg: 'bg-orange-100', text: 'text-orange-700', icon: Lock },
      'login_blocked': { bg: 'bg-red-100', text: 'text-red-700', icon: Ban },
      'user_registered': { bg: 'bg-blue-100', text: 'text-blue-700', icon: UserCheck },
      'password_changed': { bg: 'bg-purple-100', text: 'text-purple-700', icon: Lock },
      'password_change_failed': { bg: 'bg-amber-100', text: 'text-amber-700', icon: AlertTriangle },
    };
    return badges[eventType] || { bg: 'bg-slate-100', text: 'text-slate-700', icon: Shield };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="container-app py-8">
          <div className="animate-pulse space-y-8">
            <div className="h-32 bg-slate-200 rounded-2xl" />
            <div className="grid grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-slate-200 rounded-xl" />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="container-app py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Admin Dashboard</h1>
            <p className="text-slate-500 mt-1">Platform management</p>
          </div>
          <Button 
            onClick={() => setCommissionModal(true)}
            variant="outline"
            className="rounded-full"
            data-testid="commission-settings-btn"
          >
            <Settings className="w-5 h-5 mr-2" />
            Commission: {stats?.commission_rate}%
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-6 rounded-2xl border border-slate-200"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.users?.total || 0}</p>
                <p className="text-slate-500 text-sm">Total Users</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white p-6 rounded-2xl border border-slate-200"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Scale className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.lawyers?.approved || 0}</p>
                <p className="text-slate-500 text-sm">Active Lawyers</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white p-6 rounded-2xl border border-slate-200"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats?.revenue?.total || 0)}</p>
                <p className="text-slate-500 text-sm">Total Revenue</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white p-6 rounded-2xl border border-slate-200"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats?.revenue?.platform_earnings || 0)}</p>
                <p className="text-slate-500 text-sm">Platform Earnings</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Visitor Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Website Visitors
            </h3>
            <a href="/stats" className="text-blue-200 text-sm hover:text-white">View Full Analytics →</a>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-white">{visitorStats?.today?.page_views || 0}</p>
              <p className="text-blue-200 text-sm">Today's Views</p>
              <p className="text-blue-300 text-xs mt-1">{visitorStats?.today?.unique_visitors || 0} unique</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-white">{visitorStats?.this_week?.page_views || 0}</p>
              <p className="text-blue-200 text-sm">This Week</p>
              <p className="text-blue-300 text-xs mt-1">{visitorStats?.this_week?.unique_visitors || 0} unique</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-white">{visitorStats?.all_time?.page_views || 0}</p>
              <p className="text-blue-200 text-sm">All Time</p>
              <p className="text-blue-300 text-xs mt-1">{visitorStats?.all_time?.unique_visitors || 0} unique</p>
            </div>
          </div>
        </motion.div>

        {/* Pending Approvals */}
        {pendingLawyers.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8"
          >
            <h3 className="font-semibold text-amber-800 mb-4">
              Pending Lawyer Approvals ({pendingLawyers.length})
            </h3>
            <div className="space-y-4">
              {pendingLawyers.map((lawyer, idx) => (
                <div 
                  key={lawyer.id}
                  className="bg-white p-4 rounded-xl border border-amber-200 flex flex-col md:flex-row md:items-center justify-between gap-4"
                  data-testid={`pending-lawyer-${idx}`}
                >
                  <div>
                    <h4 className="font-medium text-slate-900">{lawyer.user?.name}</h4>
                    <p className="text-sm text-slate-500">
                      {lawyer.qualification} • {lawyer.years_of_experience} years • Bar: {lawyer.bar_council_number}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {lawyer.specializations?.map(spec => (
                        <span key={spec} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                          {spec}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setLawyerDetailModal({ open: true, lawyer })}
                      data-testid={`view-lawyer-${idx}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => approveLawyer(lawyer.id)}
                      data-testid={`approve-lawyer-${idx}`}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => rejectLawyer(lawyer.id)}
                      data-testid={`reject-lawyer-${idx}`}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Main Tabs */}
        <div className="bg-white rounded-2xl border border-slate-200">
          <Tabs defaultValue="users">
            <TabsList className="w-full border-b border-slate-200 rounded-t-2xl">
              <TabsTrigger value="users" className="flex-1" data-testid="tab-users">
                Users ({users.length})
              </TabsTrigger>
              <TabsTrigger value="bookings" className="flex-1" data-testid="tab-bookings">
                Bookings ({bookings.length})
              </TabsTrigger>
              <TabsTrigger value="payments" className="flex-1" data-testid="tab-payments">
                Payments ({payments.length})
              </TabsTrigger>
              <TabsTrigger 
                value="security" 
                className="flex-1" 
                data-testid="tab-security"
                onClick={() => !securityStats && fetchSecurityData()}
              >
                <Shield className="w-4 h-4 mr-1" />
                Security
              </TabsTrigger>
            </TabsList>

            <TabsContent value="users" className="p-6">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Registered</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u, idx) => {
                      const userStatus = u.status || (u.is_active === false ? 'suspended' : 'active');
                      const isActive = userStatus === 'active' && u.is_active !== false;
                      const isSuspended = userStatus === 'suspended' || u.is_active === false;
                      
                      return (
                        <TableRow key={u.id} data-testid={`user-row-${idx}`}>
                          <TableCell className="font-medium">
                            <div>
                              {u.name}
                              {u.suspended_reason && (
                                <p className="text-xs text-red-500 mt-1">
                                  Suspended: {u.suspended_reason}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              u.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                              u.role === 'lawyer' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {u.role}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {isActive ? 'Active' : 'Suspended'}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            {u.role !== 'admin' && (
                              <div className="flex gap-1 justify-end">
                                {/* View History */}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => fetchActionLogs(u)}
                                  title="View Action History"
                                  data-testid={`history-user-${idx}`}
                                >
                                  <History className="w-4 h-4" />
                                </Button>
                                
                                {/* Suspend/Activate */}
                                {isActive ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-amber-600 border-amber-300 hover:bg-amber-50"
                                    onClick={() => setSuspendModal({ open: true, user: u })}
                                    data-testid={`suspend-user-${idx}`}
                                  >
                                    <Ban className="w-4 h-4 mr-1" />
                                    Suspend
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                                    onClick={() => activateUser(u.id)}
                                    data-testid={`activate-user-${idx}`}
                                  >
                                    <UserCheck className="w-4 h-4 mr-1" />
                                    Activate
                                  </Button>
                                )}
                                
                                {/* Remove */}
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => setRemoveModal({ open: true, user: u })}
                                  data-testid={`remove-user-${idx}`}
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Remove
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="bookings" className="p-6">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Lawyer</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Mode</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bookings.map((b, idx) => (
                      <TableRow key={b.id} data-testid={`booking-row-${idx}`}>
                        <TableCell>{b.client?.name || 'N/A'}</TableCell>
                        <TableCell>{b.lawyer?.user?.name || 'N/A'}</TableCell>
                        <TableCell>{b.date} {b.time_slot}</TableCell>
                        <TableCell className="capitalize">{b.consultation_mode}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            b.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                            b.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            b.status === 'accepted' ? 'bg-blue-100 text-blue-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {b.status}
                          </span>
                        </TableCell>
                        <TableCell>{formatCurrency(b.consultation_fee)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="payments" className="p-6">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Payment ID</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Platform Fee</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((p, idx) => (
                      <TableRow key={p.id} data-testid={`payment-row-${idx}`}>
                        <TableCell className="font-mono text-sm">{p.id.slice(0, 8)}...</TableCell>
                        <TableCell>{formatCurrency(p.amount)}</TableCell>
                        <TableCell className="text-emerald-600">{formatCurrency(p.platform_fee)}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            p.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {p.status}
                          </span>
                        </TableCell>
                        <TableCell>{new Date(p.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* Security Tab */}
            <TabsContent value="security" className="p-6">
              {securityLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Security Stats Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <ShieldCheck className="w-5 h-5 text-emerald-600" />
                        <span className="text-sm font-medium text-emerald-700">Successful Logins</span>
                      </div>
                      <p className="text-2xl font-bold text-emerald-800">
                        {securityStats?.total_successful_logins || 0}
                      </p>
                    </div>
                    
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <ShieldAlert className="w-5 h-5 text-red-600" />
                        <span className="text-sm font-medium text-red-700">Failed Logins Today</span>
                      </div>
                      <p className="text-2xl font-bold text-red-800">
                        {securityStats?.failed_logins_today || 0}
                      </p>
                    </div>
                    
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Lock className="w-5 h-5 text-orange-600" />
                        <span className="text-sm font-medium text-orange-700">Lockouts Today</span>
                      </div>
                      <p className="text-2xl font-bold text-orange-800">
                        {securityStats?.lockouts_today || 0}
                      </p>
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <UserCheck className="w-5 h-5 text-blue-600" />
                        <span className="text-sm font-medium text-blue-700">New Registrations</span>
                      </div>
                      <p className="text-2xl font-bold text-blue-800">
                        {securityStats?.new_registrations_today || 0}
                      </p>
                    </div>
                  </div>

                  {/* Active Threats Summary */}
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                    <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                      <Activity className="w-4 h-4" />
                      Active Security Status
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Rate Limited IPs:</span>
                        <span className="font-medium">{securityStats?.active_rate_limits || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Locked Out IPs:</span>
                        <span className="font-medium text-red-600">{securityStats?.locked_out_ips || 0}</span>
                      </div>
                    </div>
                  </div>

                  {/* Refresh Button */}
                  <div className="flex justify-end">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={fetchSecurityData}
                      disabled={securityLoading}
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${securityLoading ? 'animate-spin' : ''}`} />
                      Refresh Data
                    </Button>
                  </div>

                  {/* Security Event Logs */}
                  <div>
                    <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                      <History className="w-4 h-4" />
                      Recent Security Events
                    </h4>
                    
                    {securityLogs.length === 0 ? (
                      <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-xl">
                        <Shield className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p>No security events recorded yet</p>
                        <p className="text-sm mt-1">Events will appear here as users interact with the platform</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Event Type</TableHead>
                              <TableHead>Details</TableHead>
                              <TableHead>IP Address</TableHead>
                              <TableHead>Timestamp</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {securityLogs.map((log, idx) => {
                              const badge = getEventBadge(log.event_type);
                              const IconComponent = badge.icon;
                              
                              return (
                                <TableRow key={log.id || idx}>
                                  <TableCell>
                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                                      <IconComponent className="w-3 h-3" />
                                      {log.event_type.replace(/_/g, ' ')}
                                    </span>
                                  </TableCell>
                                  <TableCell className="max-w-xs">
                                    <div className="text-sm">
                                      {log.details?.user_id && (
                                        <p className="text-slate-600 truncate">
                                          User: {log.details.user_id.slice(0, 8)}...
                                        </p>
                                      )}
                                      {log.details?.email_attempted && (
                                        <p className="text-slate-600 truncate">
                                          Email: {log.details.email_attempted}
                                        </p>
                                      )}
                                      {log.details?.reason && (
                                        <p className="text-slate-500 text-xs">
                                          Reason: {log.details.reason}
                                        </p>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                                      {log.details?.ip_address || 'N/A'}
                                    </code>
                                  </TableCell>
                                  <TableCell className="text-sm text-slate-500">
                                    {log.timestamp ? new Date(log.timestamp).toLocaleString() : 'N/A'}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Commission Modal */}
      <Dialog open={commissionModal} onOpenChange={setCommissionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Platform Commission Settings</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Commission Percentage
            </label>
            <Input
              type="number"
              value={newCommission}
              onChange={(e) => setNewCommission(parseFloat(e.target.value))}
              min="0"
              max="100"
              step="0.5"
              data-testid="commission-input"
            />
            <p className="text-sm text-slate-500 mt-2">
              This percentage will be deducted from each payment as platform fee.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommissionModal(false)}>
              Cancel
            </Button>
            <Button onClick={updateCommission} data-testid="save-commission-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lawyer Detail Modal */}
      <Dialog open={lawyerDetailModal.open} onOpenChange={(open) => setLawyerDetailModal({ ...lawyerDetailModal, open })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Lawyer Details</DialogTitle>
          </DialogHeader>
          {lawyerDetailModal.lawyer && (
            <div className="py-4 space-y-4">
              <div>
                <p className="text-sm text-slate-500">Name</p>
                <p className="font-medium">{lawyerDetailModal.lawyer.user?.name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Email</p>
                <p className="font-medium">{lawyerDetailModal.lawyer.user?.email}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Bar Council Number</p>
                <p className="font-medium">{lawyerDetailModal.lawyer.bar_council_number}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Qualification</p>
                <p className="font-medium">{lawyerDetailModal.lawyer.qualification}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Experience</p>
                <p className="font-medium">{lawyerDetailModal.lawyer.years_of_experience} years</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Consultation Fee</p>
                <p className="font-medium">{formatCurrency(lawyerDetailModal.lawyer.consultation_fee)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Specializations</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {lawyerDetailModal.lawyer.specializations?.map(spec => (
                    <span key={spec} className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs">
                      {spec}
                    </span>
                  ))}
                </div>
              </div>
              {lawyerDetailModal.lawyer.bio && (
                <div>
                  <p className="text-sm text-slate-500">Bio</p>
                  <p className="text-sm">{lawyerDetailModal.lawyer.bio}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLawyerDetailModal({ open: false, lawyer: null })}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend User Modal */}
      <Dialog open={suspendModal.open} onOpenChange={(open) => {
        setSuspendModal({ ...suspendModal, open });
        if (!open) setSuspendReason('');
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Suspend User
            </DialogTitle>
            <DialogDescription>
              This will prevent the user from logging in and hide their profile from public pages.
            </DialogDescription>
          </DialogHeader>
          {suspendModal.user && (
            <div className="py-4 space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="font-medium">{suspendModal.user.name}</p>
                <p className="text-sm text-slate-500">{suspendModal.user.email}</p>
                <span className={`mt-2 inline-block px-2 py-1 rounded-full text-xs font-medium ${
                  suspendModal.user.role === 'lawyer' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'
                }`}>
                  {suspendModal.user.role}
                </span>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Reason for suspension (optional)
                </label>
                <Textarea
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  placeholder="Enter the reason for suspending this user..."
                  rows={3}
                  data-testid="suspend-reason-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendModal({ open: false, user: null })}>
              Cancel
            </Button>
            <Button 
              onClick={suspendUser} 
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-suspend-btn"
            >
              <Ban className="w-4 h-4 mr-2" />
              Suspend User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove User Modal */}
      <Dialog open={removeModal.open} onOpenChange={(open) => {
        setRemoveModal({ ...removeModal, open });
        if (!open) {
          setRemoveReason('');
          setArchiveOnRemove(true);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Permanently Remove User
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. The user and all their data will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          {removeModal.user && (
            <div className="py-4 space-y-4">
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                <p className="font-medium text-red-900">{removeModal.user.name}</p>
                <p className="text-sm text-red-700">{removeModal.user.email}</p>
                <span className={`mt-2 inline-block px-2 py-1 rounded-full text-xs font-medium ${
                  removeModal.user.role === 'lawyer' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'
                }`}>
                  {removeModal.user.role}
                </span>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Reason for removal (optional)
                </label>
                <Textarea
                  value={removeReason}
                  onChange={(e) => setRemoveReason(e.target.value)}
                  placeholder="Enter the reason for removing this user..."
                  rows={3}
                  data-testid="remove-reason-input"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="archive-checkbox"
                  checked={archiveOnRemove}
                  onChange={(e) => setArchiveOnRemove(e.target.checked)}
                  className="rounded border-slate-300"
                />
                <label htmlFor="archive-checkbox" className="text-sm text-slate-600">
                  Archive user data for compliance records
                </label>
              </div>

              <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Warning:</strong> This will delete the user's account, profile, bookings, messages, and all associated data.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveModal({ open: false, user: null })}>
              Cancel
            </Button>
            <Button 
              onClick={removeUser} 
              variant="destructive"
              data-testid="confirm-remove-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Permanently Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Logs Modal */}
      <Dialog open={actionLogsModal.open} onOpenChange={(open) => setActionLogsModal({ ...actionLogsModal, open })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Action History
            </DialogTitle>
            {actionLogsModal.user && (
              <DialogDescription>
                History for {actionLogsModal.user.name} ({actionLogsModal.user.email})
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="py-4">
            {actionLogsModal.logs.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No action history found for this user.</p>
            ) : (
              <div className="space-y-3">
                {actionLogsModal.logs.map((log, idx) => (
                  <div 
                    key={log.id || idx} 
                    className={`p-4 rounded-lg border ${
                      log.action === 'suspend' ? 'bg-amber-50 border-amber-200' :
                      log.action === 'remove' ? 'bg-red-50 border-red-200' :
                      'bg-emerald-50 border-emerald-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        log.action === 'suspend' ? 'bg-amber-100 text-amber-700' :
                        log.action === 'remove' ? 'bg-red-100 text-red-700' :
                        'bg-emerald-100 text-emerald-700'
                      }`}>
                        {log.action === 'suspend' ? 'Suspended' :
                         log.action === 'remove' ? 'Removed' :
                         'Activated'}
                      </span>
                      <span className="text-xs text-slate-500">
                        {new Date(log.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600">
                      <strong>By:</strong> {log.admin_name} ({log.admin_email})
                    </p>
                    {log.reason && (
                      <p className="text-sm text-slate-600 mt-1">
                        <strong>Reason:</strong> {log.reason}
                      </p>
                    )}
                    {log.archived !== undefined && (
                      <p className="text-sm text-slate-600 mt-1">
                        <strong>Data Archived:</strong> {log.archived ? 'Yes' : 'No'}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionLogsModal({ open: false, user: null, logs: [] })}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
